import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Search, FileText, ShoppingCart, CheckSquare, Users,
  LayoutDashboard, Bell, Settings, ChevronLeft, ChevronRight,
  Shield, ClipboardList, UserCog, Activity, Truck, RotateCcw,
  Building2,
} from "lucide-react";
import { cn } from "../../lib/utils";

const navSections = [
  {
    title: "MAIN",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: [] },
      { to: "/inquiry", icon: Search, label: "Product Inquiry", roles: ["Sales Agent", "Inquiry Department", "Admin"] },
    ],
  },
  {
    title: "SALES",
    items: [
      { to: "/quotations", icon: FileText, label: "Quotations", roles: ["Sales Agent", "Approver", "Branch Manager", "Admin"] },
      { to: "/orders", icon: ShoppingCart, label: "Orders", roles: ["Sales Agent", "Approver", "Branch Manager", "Accounting", "Admin"] },
      { to: "/customers", icon: Building2, label: "Customers", roles: ["Sales Agent", "Admin"] },
    ],
  },
  {
    title: "WORKFLOW",
    items: [
      { to: "/approvals", icon: CheckSquare, label: "Approvals", roles: ["Approver", "Branch Manager", "Treasury", "Admin"] },
      { to: "/notifications", icon: Bell, label: "Notifications", roles: [] },
    ],
  },
  {
    title: "ADMIN",
    items: [
      { to: "/admin/users", icon: UserCog, label: "Users", roles: ["Admin"] },
      { to: "/admin/roles", icon: Shield, label: "Roles", roles: ["Admin"] },
      { to: "/admin/audit", icon: ClipboardList, label: "Audit Logs", roles: ["Admin"] },
      { to: "/admin/sap", icon: Activity, label: "SAP Monitor", roles: ["Admin"] },
      { to: "/settings", icon: Settings, label: "Settings", roles: ["Admin"] },
    ],
  },
  {
    title: "SCAFFOLD",
    items: [
      { to: "/delivery", icon: Truck, label: "Delivery", roles: ["Admin"], scaffold: true },
      { to: "/returns", icon: RotateCcw, label: "Returns", roles: ["Admin"], scaffold: true },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const userRoles = user?.roles || [];

  const hasAccess = (itemRoles) => {
    if (!itemRoles || itemRoles.length === 0) return true;
    if (userRoles.includes("Admin")) return true;
    return itemRoles.some((r) => userRoles.includes(r));
  };

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "h-screen bg-white border-r border-gray-200 flex flex-col transition-all duration-200 ease-in-out sticky top-0 z-30",
        collapsed ? "w-[60px]" : "w-[240px]"
      )}
    >
      <div className="h-14 flex items-center justify-between px-3 border-b border-gray-200">
        {!collapsed && (
          <span className="font-black text-lg tracking-tighter text-[#0052CC]" style={{ fontFamily: "Chivo, sans-serif" }}>
            ARCHON
          </span>
        )}
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {navSections.map((section) => {
          const visibleItems = section.items.filter((item) => hasAccess(item.roles));
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title} className="mb-2">
              {!collapsed && (
                <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                  {section.title}
                </div>
              )}
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={cn(
                      "flex items-center gap-2.5 mx-2 px-2.5 py-2 rounded-sm text-sm transition-colors duration-200",
                      isActive
                        ? "bg-[#0052CC] text-white font-medium"
                        : "text-gray-700 hover:bg-gray-100",
                      item.scaffold && "opacity-50"
                    )}
                  >
                    <Icon size={18} strokeWidth={isActive ? 2 : 1.5} />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-gray-200 p-3">
        {!collapsed && user && (
          <div className="text-xs text-gray-500 truncate">
            <div className="font-medium text-gray-800 truncate">{user.name}</div>
            <div className="truncate">{userRoles.join(", ")}</div>
          </div>
        )}
      </div>
    </aside>
  );
}
